import React, { useState, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import DescriptionIcon from '@mui/icons-material/Description';

import { MedicaidContext } from '../../contexts/MedicaidContext';
import { EXCLUDED_FIELDS, formatLabel, getContextKeys, buildMedicaidDocHtml } from './medicaidDocExport';

// Determine if we're in development/edit mode
// Use REACT_APP_MODE to control this (NODE_ENV is controlled by build system)
const isDevelopment = process.env.REACT_APP_MODE === 'development';  

function ManageMedicaidVariables() {
  const { 
    variables, 
    loading, 
    error, 
    getVariables, 
    updateVariables, 
    syncAllFromStaging, 
    setError,
    // History
    history,
    historySnapshot,
    selectedHistoryDate,
    getHistory,
    getHistorySnapshot,
    clearHistorySelection
  } = useContext(MedicaidContext);
  
  const [selectedState, setSelectedState] = useState('');
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [restoring, setRestoring] = useState(false);
  
  // Track if viewing historical data
  const viewingHistory = !!selectedHistoryDate;

  // Fetch variables on mount
  useEffect(() => {
    getVariables();
  }, []);

  // When variables load, auto-select first state if available
  useEffect(() => {
    if (variables.length > 0 && !selectedState) {
      setSelectedState(variables[0].state);
    }
  }, [variables, selectedState]);

  // When selected state changes, load that state's data into form
  useEffect(() => {
    if (selectedState && variables.length > 0 && !viewingHistory) {
      const stateData = variables.find(v => v.state === selectedState);
      if (stateData) {
        // Deep clone to avoid mutating context state
        setFormData(JSON.parse(JSON.stringify(stateData)));
      } else {
        // New state - initialize with empty structure
        setFormData({
          state: selectedState,
          static_variables: {},
        });
      }
    }
  }, [selectedState, variables, viewingHistory]);

  // When viewing history, load the historical snapshot into form
  useEffect(() => {
    if (historySnapshot && viewingHistory) {
      setFormData(JSON.parse(JSON.stringify(historySnapshot)));
    }
  }, [historySnapshot, viewingHistory]);

  // Fetch history when state changes (only in development mode)
  useEffect(() => {
    if (isDevelopment && selectedState) {
      getHistory(selectedState);
    }
  }, [selectedState]);

  const handleStateChange = (e) => {
    clearHistorySelection(); // Clear history selection when state changes
    setSelectedState(e.target.value);
  };

  const handleHistoryChange = async (e) => {
    const dateValue = e.target.value;
    if (!dateValue) {
      // "Current" selected - clear history and revert to current data
      clearHistorySelection();
    } else {
      // Fetch the historical snapshot
      await getHistorySnapshot(selectedState, dateValue);
    }
  };

  const handleRestore = async () => {
    if (!selectedState || !historySnapshot) return;
    
    setRestoring(true);
    // Use skipHistory: true to avoid creating a new history entry when restoring
    const result = await updateVariables(selectedState, historySnapshot, { skipHistory: true });
    setRestoring(false);
    
    if (result.success) {
      clearHistorySelection();
      setSuccessMessage(`Successfully restored ${selectedState} to version from ${new Date(selectedHistoryDate).toLocaleString()}`);
    }
  };

  const handleActiveChange = (e) => {
    const { checked } = e.target;
    setFormData(prev => ({ ...prev, active: checked }));
  };

  const handleStaticVariableChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      static_variables: {
        ...prev.static_variables,
        [key]: value === '' ? '' : Number(value)
      }
    }));
  };

  const handleContextFieldChange = (contextKey, fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [contextKey]: {
        ...prev[contextKey],
        [fieldKey]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedState || !formData) return;
    
    setSaving(true);
    const result = await updateVariables(selectedState, formData);
    setSaving(false);
    
    if (result.success) {
      setSuccessMessage(`Successfully saved variables for ${selectedState}`);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    const result = await syncAllFromStaging();
    setSyncing(false);
    
    if (result.success) {
      setSuccessMessage(`Successfully synced all states from staging`);
    }
  };

  const handleExportWord = () => {
    if (!formData) return;
    const html = buildMedicaidDocHtml(formData);
    // Prepend BOM so Word reads the UTF-8 content correctly.
    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${formData.state || 'medicaid'}-medicaid-config.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError(null);
  };

  // Get list of available states from the variables data
  const availableStates = variables.map(v => v.state);

  if (loading && variables.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">
            Manage AI Context Data
          </Typography>
          <Chip
            icon={isDevelopment ? <EditIcon /> : <VisibilityIcon />}
            label={isDevelopment ? 'Edit Mode' : 'View Only'}
            color={isDevelopment ? 'success' : 'default'}
            variant="outlined"
            size="small"
          />
        </Box>
        {!isDevelopment && (
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={syncing || loading}
            startIcon={syncing ? null : <SyncIcon />}
          >
            {syncing ? <CircularProgress size={20} color="inherit" /> : 'Sync All from Staging'}
          </Button>
        )}
      </Box>

      {/* State and History Selectors */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>State</InputLabel>
          <Select
            value={selectedState}
            label="State"
            onChange={handleStateChange}
          >
            {availableStates.map(state => (
              <MenuItem key={state} value={state}>{state}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* History Selector - Only in development mode */}
        {isDevelopment && (
          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HistoryIcon fontSize="small" />
                History
              </Box>
            </InputLabel>
            <Select
              value={selectedHistoryDate || ''}
              label="History"
              onChange={handleHistoryChange}
              disabled={!selectedState || history.length === 0}
            >
              <MenuItem value="">
                <em>Current Version</em>
              </MenuItem>
              {history.map((item) => (
                <MenuItem key={item.archived_date} value={item.archived_date}>
                  {new Date(item.archived_date).toLocaleString()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={handleExportWord}
          disabled={!formData}
          sx={{ alignSelf: 'center' }}
        >
          Export to Word
        </Button>
      </Box>

      {/* Historical Data Banner - Only in development mode */}
      {isDevelopment && viewingHistory && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
              onClick={handleRestore}
              disabled={restoring || loading}
            >
              {restoring ? 'Restoring...' : 'Restore This Version'}
            </Button>
          }
        >
          Viewing historical snapshot from {new Date(selectedHistoryDate).toLocaleString()}
        </Alert>
      )}

      {formData && (
        <>
          {/* State Availability Toggle - Only in development mode */}
          {isDevelopment && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  State Availability
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active states appear on the marketing site and can be screened. Turn off for "coming soon" states.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active !== false}
                    onChange={handleActiveChange}
                    disabled={viewingHistory}
                  />
                }
                label={formData.active !== false ? 'Active' : 'Coming soon'}
              />
            </Box>
          )}

          {/* Static Variables Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Static Variables</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                {formData.static_variables && Object.keys(formData.static_variables)
                  .filter(key => !EXCLUDED_FIELDS.includes(key))
                  .map(key => (
                  <TextField
                    key={key}
                    label={formatLabel(key)}
                    type="number"
                    value={formData.static_variables[key] ?? ''}
                    onChange={(e) => handleStaticVariableChange(key, e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: 'any' }}
                    InputProps={{ readOnly: !isDevelopment || viewingHistory }}
                    disabled={!isDevelopment || viewingHistory}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Context Sections */}
          {getContextKeys(formData).map(contextKey => (
            <Accordion key={contextKey}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  {formatLabel(contextKey.replace('context_', ''))}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {formData[contextKey] && Object.keys(formData[contextKey])
                    .filter(fieldKey => !EXCLUDED_FIELDS.includes(fieldKey))
                    .map(fieldKey => (
                    <TextField
                      key={fieldKey}
                      label={formatLabel(fieldKey)}
                      multiline
                      rows={4}
                      value={formData[contextKey][fieldKey] ?? ''}
                      onChange={(e) => handleContextFieldChange(contextKey, fieldKey, e.target.value)}
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: !isDevelopment || viewingHistory }}
                      disabled={!isDevelopment || viewingHistory}
                      sx={{ '& textarea': { resize: 'vertical' } }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Save Button - Only in development mode and not viewing history */}
          {isDevelopment && !viewingHistory && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSave}
                disabled={saving || loading}
                sx={{ minWidth: 200 }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ManageMedicaidVariables;
